import React from 'react'
// @src imports
import {
  useAccount,
  useActions,
  useFlagsWithUseQueries,
  useSelector,
} from 'src/hooks'
import { Transition } from 'src/chrome'
import suspenseBoundary from 'src/chrome/SuspenseBoundary/suspenseBoundaryHOC'

import { Flex } from '@sendoutcards/quantum-design-ui'
import { TabBar } from 'src/design_system/components/TabBar/TabBar'
import { IconType } from 'src/design_system/atoms/icons/types'
import { AccountRoute } from 'src/dashboard/routes/AccountRoute'
import { PaymentSettingsRoute } from 'src/dashboard/routes/PaymentSettingsRoute'
import { CardHistoryRoute } from 'src/dashboard/routes/CardHistoryRoute'
import { DraftedOrdersRoute } from 'src/dashboard/routes/DraftedOrdersRoute'
import { ShareSettingsRoute } from 'src/dashboard/routes/ShareSettingsRoute'
import { DashboardSettingsRoute } from 'src/dashboard/routes/Settings'
import {
  useNotificationCount,
  useReceivedPendingMembershipInvites,
} from 'src/react_query'
import { MyGiftsRoute } from 'src/dashboard/routes/MyGiftsRoute'
import { MembershipRewards } from 'src/dashboard/components/MembershipRewards/MembershipRewards'

const styles = {
  dashboardHeader: {
    position: 'static' as const,
    width: '100%',
    height: 'auto',
    backgroundColor: '#F5F6F7',
    overflow: 'visible',
    padding: 0,
    display: 'flex',
    flexDlow: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 1000,
    '@media (max-width: 815px)': {
      justifyContent: 'center',
      columnGap: '16px',
    },
  },
  headerSubtitle: {
    display: 'flex',
    alignItems: 'baseline',
    '@media (max-width: 1550px)': {
      flexDirection: 'column' as const,
    },
  },
  flexRowWrap: {
    display: 'flex',
    flexFlow: 'row wrap',
    width: '100%',
    justifyContent: 'center',
    overflow: 'visible',
  },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
    '@media (max-width: 670px)': {
      alignItems: 'flex-start',
      paddingLeft: 20,
    },
    '@media (max-width: 500px)': {
      alignItems: 'center',
      paddingLeft: 0,
    },
  },
}

type DashboardHeaderProps = {
  routePath: string
  toggleReminders: () => void
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  routePath,
  toggleReminders,
}) => {
  const actions = useActions()

  const { hasGiftCardWallet, hasMembershipRewards } = useFlagsWithUseQueries()
  const { data: notificationCount } = useNotificationCount()
  const { data: pendingMembershipInvites } =
    useReceivedPendingMembershipInvites()

  const notificationsCounter =
    (notificationCount ?? 0) + (pendingMembershipInvites?.length ?? 0)

  const tabs: {
    onClick: () => void
    title: string
    icon: IconType
    routePath?: string
    notificationCount?: number
    hideText?: boolean
    showLeftDivider?: boolean
  }[] = [
    {
      onClick: () => actions.openAccount(),
      title: 'Home',
      icon: 'house',
      routePath: AccountRoute.path,
    },
    {
      onClick: () => actions.openPaymentSettings(),
      title: 'My Account',
      icon: 'profile',
      routePath: PaymentSettingsRoute.path,
    },
    {
      onClick: () => actions.openCardHistory(),
      title: 'Card History',
      icon: 'card_history',
      routePath: CardHistoryRoute.path,
    },
    {
      onClick: () => actions.openDraftedOrders(),
      title: 'My Drafts',
      icon: 'drafts',
      routePath: DraftedOrdersRoute.path,
    },
    ...(hasGiftCardWallet
      ? [
          {
            onClick: () => actions.openMyGifts(),
            title: 'My Gift Cards',
            icon: 'gift' as IconType,
            routePath: MyGiftsRoute.path,
          },
        ]
      : []),
    {
      onClick: () => actions.openShareSettings(),
      title: 'Sharing',
      icon: 'share_settings',
      routePath: ShareSettingsRoute.path,
    },
    {
      onClick: () => actions.openDashboardSettingsRoute(),
      title: 'Settings',
      icon: 'settings',
      routePath: DashboardSettingsRoute.path,
    },
    {
      onClick: () => toggleReminders(),
      title: 'Notifications',
      icon: 'notifications',
      notificationCount: notificationsCounter,
      hideText: true,
      showLeftDivider: true,
    },
  ]
  const matchTabToRoutPath = tabs.findIndex(tab => tab.routePath === routePath)
  const defaultTabIndex = matchTabToRoutPath < 0 ? 0 : matchTabToRoutPath
  const account = useAccount()
  const membershipRewardsAmount = account.points?.amount ?? 0
  const window = useSelector(state => state.window)
  const shouldHideTabBarText = window.width <= 1150
  const shouldShowMembershipRewardsPill = window.width >= 600
  const displayTabs = tabs

  return (
    <Flex css={styles.dashboardHeader}>
      <div css={styles.flexRowWrap}>
        <Flex
          columnGap="x4"
          justifyContent="center"
          style={{ overflow: 'visible' }}
        >
          <div
            css={{
              maxWidth: '100%',
              overflowX: 'auto',
              padding: '12px',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none' as const,
              '::-webkit-scrollbar': {
                display: 'none' as const,
                width: 0,
                height: 0,
              },
            }}
          >
            <TabBar
              tabs={displayTabs}
              activeTabIndex={defaultTabIndex}
              activeTabBackgroundColor="#BE9CDD"
              activeTabTextColor="#FFF"
              barBackgroundColor="#fff"
              barBoxShadow="0px 4px 8px 0px rgba(0, 0, 0, 0.08)"
              tabTextSize="13px"
              tabInsetVertical="x2"
              shouldHideText={shouldHideTabBarText}
              shouldTabsFillWidth={false}
            />
          </div>
          {shouldShowMembershipRewardsPill && hasMembershipRewards && (
            <MembershipRewards
              amount={membershipRewardsAmount}
              onClick={() => actions.openPaymentSettings()}
            />
          )}
        </Flex>
      </div>
    </Flex>
  )
}

export default suspenseBoundary({
  component: DashboardHeader,
  unresolved: <Transition />,
  failure: error => `Failed loading: ${error}`,
})
